/**
 * Quote lineage — revisions and quote → invoice conversion (backlog N/Rule
 * 18). A lineage is a chain of records describing the same piece of work:
 *
 *   Q-1020 (superseded) ──▶ Q-1020 R1 (accepted) ──▶ INV-0442
 *
 * Two rules keep the chain trustworthy, mirrored from @/lib/types' field
 * comments:
 * 1. Nothing is edited in place once delivered — a price/scope change after
 *    the client has the paper creates a NEW record via createRevisionDraft.
 * 2. The predecessor is only superseded/invoiced when the successor is SENT
 *    (see @/lib/documents's sendDocument), never on draft creation, so an
 *    abandoned draft can't orphan the original.
 *
 * Chain membership is resolved by walking `supersedesDocKey`/
 * `supersededByDocKey`/`derivedFromDocKey` pointers, never by grouping on
 * `docId` — the document number is user-editable free text
 * (@/lib/docNumbering: "still editable, subs who run their own numbering
 * can overtype it"), so two unrelated documents could coincidentally share
 * one; only explicit docKey pointers are safe to treat as ground truth.
 */
import { getDocument, saveDocument, docKeyExists, type DocumentListItem } from "@/lib/documents";
import { nextInvoiceNumber } from "@/lib/docNumbering";
import { slugify, todayDisplay } from "@/features/quotes/quoteMath";
import type { AnyDocument, Invoice, StandardQuote } from "@/lib/types";

/** "Q-2026-0106 R1" when revised, else the plain root number. */
export function displayNumber(doc: Pick<StandardQuote, "docId" | "lineageRevision">): string {
  const rev = doc.lineageRevision ?? 0;
  return rev > 0 ? `${doc.docId} R${rev}` : doc.docId;
}

/** Display number for any document in a chain — the R-suffix form for a quote/revision, the plain root for an invoice (invoices aren't themselves revised in this pass). */
export function chainLabel(doc: AnyDocument): string {
  return doc.docType === "standard" ? displayNumber(doc) : doc.docId;
}

/** A quote can be revised once the client has seen it — a draft is still freely editable in place, and a closed/void/superseded/invoiced record is history. */
export function canRevise(doc: Pick<AnyDocument, "docType" | "status">): boolean {
  return doc.docType === "standard" && (doc.status === "pending" || doc.status === "accepted");
}

/** The live invoice billing this quote, if one has already been created. */
export function invoiceFor(sourceDocKey: string, all: DocumentListItem[]): DocumentListItem | null {
  return (
    all.find(
      (item) => item.doc.docType === "invoice" && item.doc.derivedFromDocKey === sourceDocKey && item.doc.status !== "void",
    ) ?? null
  );
}

/** Only an accepted quote can be billed, and only once — a second attempt should open the existing invoice instead of creating another. */
export function canInvoice(docKey: string, doc: Pick<StandardQuote, "docType" | "status">, all: DocumentListItem[]): boolean {
  return doc.docType === "standard" && doc.status === "accepted" && !invoiceFor(docKey, all);
}

/**
 * Every record in this document's chain: resolve to the quote anchor (the
 * target itself if it's a quote, or the quote an invoice bills), walk
 * `supersedesDocKey` backward to the root and `supersededByDocKey` forward
 * through every revision from there, then append any Invoice whose
 * `derivedFromDocKey` points at a docKey in that chain. Oldest quote first,
 * invoice (if any) last. A standalone invoice (no `derivedFromDocKey`) has
 * no anchor and returns just itself.
 */
export function lineageOf(target: DocumentListItem, all: DocumentListItem[]): DocumentListItem[] {
  const byKey = new Map(all.map((item) => [item.docKey, item]));

  const anchor: DocumentListItem | undefined =
    target.doc.docType === "standard"
      ? target
      : target.doc.docType === "invoice" && target.doc.derivedFromDocKey
        ? byKey.get(target.doc.derivedFromDocKey)
        : undefined;
  if (!anchor) return [target];

  const chain: DocumentListItem[] = [];
  let cursor: DocumentListItem | undefined = anchor;
  while (cursor?.doc.docType === "standard" && cursor.doc.supersedesDocKey) {
    const prev: DocumentListItem | undefined = byKey.get(cursor.doc.supersedesDocKey);
    if (!prev) break;
    chain.unshift(prev);
    cursor = prev;
  }
  chain.push(anchor);
  cursor = anchor;
  while (cursor?.doc.docType === "standard" && cursor.doc.supersededByDocKey) {
    const next: DocumentListItem | undefined = byKey.get(cursor.doc.supersededByDocKey);
    if (!next) break;
    chain.push(next);
    cursor = next;
  }

  const chainKeys = new Set(chain.map((item) => item.docKey));
  const invoice = all.find((item) => item.doc.docType === "invoice" && chainKeys.has(item.doc.derivedFromDocKey ?? ""));
  if (invoice) chain.push(invoice);

  return chain;
}

/** A chain with more than one record is worth showing a trail for. */
export function hasLineage(target: DocumentListItem, all: DocumentListItem[]): boolean {
  return lineageOf(target, all).length > 1;
}

async function persistNew(doc: AnyDocument): Promise<string> {
  let key = slugify(doc.docId);
  if (await docKeyExists(key)) key = `${key}-${Date.now()}`;
  await saveDocument(key, doc);
  return key;
}

/**
 * Clones a delivered quote into a draft revision — same root `docId`, next
 * `lineageRevision`, items deep-copied with their original ids PRESERVED
 * (so @/features/quotes/quoteMath's lineDelta can match lines by identity).
 * The source is left completely untouched here; it only becomes
 * `superseded` once this draft is sent (see @/lib/documents's
 * sendDocument).
 */
export async function createRevisionDraft(sourceDocKey: string, reason: string): Promise<string> {
  const source = await getDocument(sourceDocKey);
  if (!source || source.docType !== "standard") throw new Error("Source quote not found");

  const draft: StandardQuote = {
    ...source,
    status: "draft",
    date: todayDisplay(),
    savedAt: new Date().toISOString(),
    dueDate: null,
    paidDate: null,
    sentDate: null,
    revision: 1,
    lineageRevision: (source.lineageRevision ?? 0) + 1,
    supersedesDocKey: sourceDocKey,
    supersededByDocKey: null,
    revisionReason: reason,
    items: source.items.map((item) => ({ ...item })),
  };
  return persistNew(draft);
}

/**
 * Opens an invoice from an accepted quote — its own `INV-YYYY-####`
 * sequence (never the quote's root number), items copied with ids
 * preserved, `derivedFromDocKey` pointing back at the quote. Throws if an
 * active invoice already exists for this quote so the caller can redirect
 * to it instead of creating a duplicate.
 */
export async function createInvoiceDraft(sourceDocKey: string, existingInvoiceDocIds: string[]): Promise<string> {
  const source = await getDocument(sourceDocKey);
  if (!source || source.docType !== "standard") throw new Error("Source quote not found");

  const draft: Invoice = {
    docType: "invoice",
    docId: nextInvoiceNumber(existingInvoiceDocIds),
    date: todayDisplay(),
    savedAt: new Date().toISOString(),
    status: "draft",
    projectName: source.projectName,
    clientId: source.clientId,
    dueDate: null,
    termId: source.termId,
    paidDate: null,
    revision: 1,
    sentDate: null,
    bizName: source.bizName,
    bizPhone: source.bizPhone,
    bizEmail: source.bizEmail,
    bizStreet: source.bizStreet,
    bizCity: source.bizCity,
    bizState: source.bizState,
    bizZip: source.bizZip,
    bizLicense: source.bizLicense,
    clientName: source.clientName,
    clientAddress: source.clientAddress,
    clientPhone: source.clientPhone,
    clientEmail: source.clientEmail,
    markup: source.markup,
    tax: source.tax,
    scopeOfWork: source.scopeOfWork,
    exclusions: source.exclusions,
    items: source.items.map((item) => ({ ...item })),
    grandTotal: source.grandTotal,
    derivedFromDocKey: sourceDocKey,
  };
  return persistNew(draft);
}
